"use client";

import posthog from "posthog-js";
import { AttributedLink } from "@/components/marketing/attributed-link";
import { useExperimentVariant } from "@/lib/experiments";

const FUNNEL_VARIANTS = ["control", "variant"] as const;

interface FunnelCtaLinkProps {
  children: React.ReactNode;
  className?: string;
  location: string;
  /**
   * Optional job-pack preset key (see `tradePresetKey`). When the funnel-v2
   * variant routes to /start-project, the preset is appended so the funnel can
   * pre-fill the scope. It is dropped for the control (/signup) destination,
   * which has nowhere to consume it.
   */
  preset?: string;
}

function withPreset(destination: string, preset?: string): string {
  if (!preset || destination !== "/start-project") return destination;
  return `${destination}?preset=${encodeURIComponent(preset)}`;
}

export function FunnelCtaLink({
  children,
  className,
  location,
  preset,
}: FunnelCtaLinkProps) {
  // funnel-v2 experiment: control → /signup, variant → /start-project.
  // Exposure ($feature_flag_called) is fired automatically by getFeatureFlag
  // inside the hook once the PostHog flag exists.
  const { variant } = useExperimentVariant("funnel-v2", {
    variants: FUNNEL_VARIANTS,
    fallback: "variant",
    overrideParam: "ab",
  });

  const destination = withPreset(
    variant === "control" ? "/signup" : "/start-project",
    preset,
  );

  return (
    <AttributedLink
      href={destination}
      className={className}
      onClick={() => {
        if (!posthog.__loaded) return;
        posthog.capture("landing_cta_clicked", {
          cta_location: location,
          "$feature/funnel-v2": variant,
          destination: destination.split("?")[0],
          ...(preset ? { preset } : {}),
          entry_path: window.location.pathname,
          referrer: document.referrer || null,
        });
      }}
    >
      {children}
    </AttributedLink>
  );
}
